import React, { useState, useEffect } from 'react';
import { membersApi } from '../api/membersApi';
import { toast } from 'react-toastify';
import {
  Layout, Typography, Button, Table, Space, Tag, Modal as AntModal,
  Card, Statistic, Row, Col, Input, Tooltip, Avatar, Empty, Spin
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  ReloadOutlined, UserOutlined, CheckCircleOutlined,
  ClockCircleOutlined, MailOutlined, LinkedinOutlined, TeamOutlined
} from '@ant-design/icons';

const { Content } = Layout;
const { Title, Text } = Typography;

const MembersManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    designation: '',
    department: '',
    profileImage: null,
    social: {
      email: '',
      linkedin: ''
    },
    priority: 0,
    isActive: true
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await membersApi.getAllMembers();
      console.log('Fetched members:', response);
      if (response.success === true ) {
        setMembers(response.data);
      } else {
        toast.error('Failed to load members');
        console.error('Invalid response format:', response);
      }
    } catch (error) {
      toast.error('Failed to fetch members');
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        profileImage: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare member data according to the server's expected format
      const memberData = {
        fullName: formData.fullName,
        designation: formData.designation,
        department: formData.department,
        social: formData.social,
        isActive: formData.isActive,
        priority: formData.priority
      };

      let response;
      if (editingMember) {
        // For updates, we need to handle the case where profileImage might be a string (URL) or File object
        const profileImage = formData.profileImage instanceof File ? formData.profileImage : null;
        response = await membersApi.updateMember(editingMember._id, memberData, profileImage);
        toast.success('Member updated successfully');
      } else {
        // For new members, we can pass the profileImage directly
        response = await membersApi.createMember(memberData, formData.profileImage);
        toast.success('Member created successfully');
      }

      if (response) {
        fetchMembers();
        resetForm();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      toast.error(`${editingMember ? 'Failed to update member' : 'Failed to create member'}: ${errorMessage}`);
      console.error('Error saving member:', error);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      fullName: member.fullName || '',
      designation: member.designation || '',
      department: member.department || '',
      profileImage: null,
      social: {
        email: member.social?.email || '',
        linkedin: member.social?.linkedin || ''
      },
      priority: member.priority || 0,
      isActive: member.isActive ?? true
    });
    setImagePreview(member.profileImage);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    AntModal.confirm({
      title: 'Delete Member',
      content: 'Are you sure you want to delete this member?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await membersApi.deleteMember(id);
          toast.success('Member deleted successfully');
          fetchMembers();
        } catch (error) {
          toast.error('Failed to delete member');
          console.error('Error deleting member:', error);
        }
      }
    });
  };

  const handleDragStart = (e, member) => {
    setDraggedItem(member);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetMember) => {
    e.preventDefault();

    if (!draggedItem || draggedItem._id === targetMember._id) return;

    const draggedIndex = members.findIndex(m => m._id === draggedItem._id);
    const targetIndex = members.findIndex(m => m._id === targetMember._id);

    const newMembers = [...members];
    newMembers.splice(draggedIndex, 1);
    newMembers.splice(targetIndex, 0, draggedItem);

    // Update priorities
    const priorityUpdates = newMembers.map((member, index) => ({
      id: member._id,
      priority: newMembers.length - index // Higher index = higher priority
    }));

    try {
      await membersApi.updateMemberPriorities(priorityUpdates);
      setMembers(newMembers);
      toast.success('Member order updated');
    } catch (error) {
      toast.error('Failed to update member order');
      console.error('Error updating priorities:', error);
    }

    setDraggedItem(null);
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      designation: '',
      department: '',
      profileImage: null,
      social: {
        email: '',
        linkedin: ''
      },
      priority: 0,
      isActive: true
    });
    setImagePreview(null);
    setEditingMember(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#ffffff', fontFamily: "'Rubik', sans-serif" }}>
        <Content style={{ padding: '32px 48px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Spin size="large" />
          </div>
        </Content>
      </Layout>
    );
  }

  // Table columns configuration
  const columns = [
    {
      title: <Text strong style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif" }}>Member</Text>,
      dataIndex: 'fullName',
      key: 'fullName',
      filteredValue: [searchText],
      onFilter: (value, record) =>
        record.fullName.toLowerCase().includes(value.toLowerCase()) ||
        (record.designation && record.designation.toLowerCase().includes(value.toLowerCase())),
      render: (text, record) => (
        <Space size={12}>
          <Avatar
            size={48}
            src={record.profileImage}
            icon={!record.profileImage && <UserOutlined />}
            style={{
              background: !record.profileImage ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : undefined,
              border: '2px solid #f0f0f0'
            }}
          />
          <div>
            <Text strong style={{ fontSize: 15, color: '#141414', display: 'block', fontFamily: "'Rubik', sans-serif" }}>
              {text}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, fontFamily: "'Rubik', sans-serif" }}>
              <UserOutlined style={{ marginRight: 4, color: '#470172' }} />
              {record.designation || 'No designation'}
            </Text>
          </div>
        </Space>
      ),
      width: 280,
    },
    {
      title: <Text strong style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif" }}>Priority</Text>,
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      render: (priority) => (
        <Text strong style={{ fontSize: 16, color: '#470172', fontFamily: "'Rubik', sans-serif" }}>
          {priority || 0}
        </Text>
      ),
      sorter: (a, b) => (a.priority || 0) - (b.priority || 0),
    },
    {
      title: <Text strong style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif" }}>Status</Text>,
      key: 'isActive',
      width: 130,
      render: (_, record) => {
        const isActive = record.isActive;
        return (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 12px',
            background: isActive ? '#f6ffed' : '#fff1f0',
            border: `1px solid ${isActive ? '#b7eb8f' : '#ffa39e'}`,
            borderRadius: 6
          }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: isActive ? '#52c41a' : '#f5222d',
              boxShadow: `0 0 0 2px ${isActive ? 'rgba(82, 196, 26, 0.2)' : 'rgba(245, 34, 45, 0.2)'}`
            }} />
            <Text style={{ fontSize: 13, color: isActive ? '#52c41a' : '#f5222d', fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>
              {isActive ? 'Active' : 'Inactive'}
            </Text>
          </div>
        );
      },
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: <Text strong style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif" }}>Actions</Text>,
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size={8}>
          <Tooltip title="Edit Member">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{
                color: '#470172',
                borderColor: '#d9d9d9',
                fontSize: 14,
                height: 32,
                fontFamily: "'Rubik', sans-serif"
              }}
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title="Delete Member">
            <Button
              type="default"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
              style={{
                fontSize: 14,
                height: 32,
                fontFamily: "'Rubik', sans-serif"
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#ffffff', fontFamily: "'Rubik', sans-serif" }}>
      <Content style={{ padding: '32px 48px' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div>
            <Title level={2} style={{
              margin: 0,
              color: '#470172',
              fontFamily: "'Rubik', sans-serif",
              fontSize: 32,
              fontWeight: 600
            }}>
              Executive Committee Members
            </Title>
            <Text style={{ fontSize: 16, color: '#595959', fontFamily: "'Rubik', sans-serif" }}>
              Manage and monitor team members and their profiles
            </Text>
          </div>
        </div>

        {/* Stats Cards */}
        <Row gutter={24} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8 }}>
              <Statistic
                title={<Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959' }}>Total Members</Text>}
                value={members.length}
                valueStyle={{ color: '#470172', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8 }}>
              <Statistic
                title={<Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959' }}>Active Members</Text>}
                value={members.filter(m => m.isActive).length}
                valueStyle={{ color: '#52c41a', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8 }}>
              <Statistic
                title={<Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959' }}>Inactive Members</Text>}
                value={members.filter(m => !m.isActive).length}
                valueStyle={{ color: '#f5222d', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card 
              bordered={false} 
              style={{ 
                background: '#470172', 
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 100
              }}
              hoverable
              onClick={() => setShowForm(true)}
            >
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <PlusOutlined style={{ fontSize: 24, color: '#fff', marginBottom: 8 }} />
                <Text style={{ 
                  fontSize: 16, 
                  fontFamily: "'Rubik', sans-serif", 
                  color: '#fff',
                  fontWeight: 500,
                  display: 'block'
                }}>
                  Add New Member
                </Text>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Search Bar */}
        <div style={{ marginBottom: 24 }}>
          <Input
            size="large"
            placeholder="Search members by name or designation..."
            prefix={<SearchOutlined style={{ color: '#470172' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              fontFamily: 'Rubik, sans-serif',
              fontSize: '16px',
              fontWeight: 400,
              color: '#470172',
              maxWidth: 600
            }}
          />
        </div>

        {/* Members Table */}
        <Card
          title={
            <div>
              <p style={{ fontFamily: 'Rubik, sans-serif', fontSize: '15px', fontWeight: 400, color: '#470172', margin: '4px 0 0 0', opacity: 0.7 }}>
                List of all Committee Members
              </p>
            </div>
          }
          extra={
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={fetchMembers}
              disabled={loading}
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                color: '#470172',
                borderColor: '#470172'
              }}
            >
              Refresh
            </Button>
          }
          bordered={false}
          style={{ background: '#fff', borderRadius: 8 }}
        >
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <Spin size="large" />
            </div>
          ) : members.length > 0 ? (
            <Table
              columns={columns}
              dataSource={members}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} members`,
                style: { fontFamily: "'Rubik', sans-serif" }
              }}
              style={{ fontFamily: "'Rubik', sans-serif" }}
              scroll={{ x: 1200 }}
            />
          ) : (
            <Empty
              description={
                <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
                  No members found. Get started by creating a new member.
                </span>
              }
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setShowForm(true)}
                size="large"
                style={{
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  backgroundColor: '#470172',
                  borderColor: '#470172'
                }}
              >
                Add First Member
              </Button>
            </Empty>
          )}
        </Card>
      </Content>

      {/* Add/Edit Member Modal */}
      <AntModal
        title={
          <span style={{ fontFamily: "'Rubik', sans-serif", fontSize: 20, fontWeight: 600, color: '#470172' }}>
            {editingMember ? 'Edit Member' : 'Add New Member'}
          </span>
        }
        open={showForm}
        onCancel={resetForm}
        footer={null}
        width={800}
        style={{ fontFamily: "'Rubik', sans-serif" }}
      >
        <form onSubmit={handleSubmit} style={{ marginTop: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                  Full Name <span style={{ color: '#f5222d' }}>*</span>
                </Text>
                <Input
                  size="large"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter full name"
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                  Designation
                </Text>
                <Input
                  size="large"
                  name="designation"
                  value={formData.designation}
                  onChange={handleInputChange}
                  placeholder="e.g., Senior Manager"
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                  Department
                </Text>
                <Input
                  size="large"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  placeholder="e.g., Operations"
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                  Priority
                </Text>
                <Input
                  size="large"
                  type="number"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  placeholder="0"
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                  Email
                </Text>
                <Input
                  size="large"
                  type="email"
                  name="social.email"
                  value={formData.social.email}
                  onChange={handleInputChange}
                  placeholder="email@example.com"
                  prefix={<MailOutlined style={{ color: '#470172' }} />}
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
                  LinkedIn URL
                </Text>
                <Input
                  size="large"
                  type="url"
                  name="social.linkedin"
                  value={formData.social.linkedin}
                  onChange={handleInputChange}
                  placeholder="https://linkedin.com/in/username"
                  prefix={<LinkedinOutlined style={{ color: '#470172' }} />}
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </div>
            </Col>
          </Row>

          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8, fontFamily: "'Rubik', sans-serif", fontSize: 14 }}>
              Profile Image
            </Text>
            <Space size={16} align="start">
              {imagePreview ? (
                <Avatar size={80} src={imagePreview} />
              ) : (
                <Avatar size={80} icon={<UserOutlined />} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} />
              )}
              <div style={{ flex: 1 }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px dashed #d9d9d9',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontFamily: "'Rubik', sans-serif",
                    fontSize: 14
                  }}
                />
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4, fontFamily: "'Rubik', sans-serif" }}>
                  PNG, JPG up to 10MB
                </Text>
              </div>
            </Space>
          </div>

          <div style={{ marginBottom: 24, padding: '12px 16px', background: '#ffffff', borderRadius: 6, border: '1px solid #f0f0f0' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontFamily: "'Rubik', sans-serif" }}>
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                style={{ marginRight: 8, width: 16, height: 16, cursor: 'pointer' }}
              />
              <Text strong style={{ fontFamily: "'Rubik', sans-serif" }}>Active Status</Text>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12, fontFamily: "'Rubik', sans-serif" }}>
                (Member will be visible to public)
              </Text>
            </label>
          </div>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
              onClick={resetForm}
              size="large"
              style={{ fontFamily: "'Rubik', sans-serif", fontSize: 14 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              style={{
                fontFamily: "'Rubik', sans-serif",
                fontSize: 14,
                backgroundColor: '#470172',
                borderColor: '#470172'
              }}
            >
              {editingMember ? 'Update Member' : 'Create Member'}
            </Button>
          </Space>
        </form>
      </AntModal>
    </Layout>
  );
};

export default MembersManagement;
